<!-- eslint-disable max-len -->
<template>
  <div class="p-t-15 p-b-15">
    <BaseCard v-if="isEnterpriseAccount">
      <template #card-header>
        <h2>{{ $t('Budget Tracker settings') }}</h2>
      </template>
      <div class="m-t-20">
        <div>
          <EditSettingsOption
            id="input-project-tracking"
            v-model="userState.projectTracking"
            :label="$t('Enable project tracking')"
            :help-info="
              $t(
                'Enables project tracking so jobs can be associated with hiring projects and real spend and projected spend can be tracked across jobs and projects',
              )
            "
            class="input-wrapper"
            @update:model-value="save('project_tracking', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'project_tracking' && saving"
              :failed="latestClickedItemKey === 'project_tracking' && savingFailed"
              size="sm"
            />
          </div>
        </div>
        <div>
          <EditSettingsOption
            id="input-required-job-budget"
            v-model="userState.requiredJobBudget"
            :label="$t('Require internal budgets on jobs and hires')"
            :help-info="$t('Job budget tracking will be required on all jobs and hires')"
            class="input-wrapper"
            @update:model-value="save('required_job_budget', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'required_job_budget' && saving"
              :failed="latestClickedItemKey === 'required_job_budget' && savingFailed"
              size="sm"
            />
          </div>
        </div>
      </div>
    </BaseCard>

    <BaseCard>
      <template #card-header>
        <h2>{{ $t('Contract settings') }}</h2>
      </template>
      <div class="m-t-20">
        <div>
          <EditSettingsOption
            v-model="userState.requireContractDateStart"
            :label="$t('Require to set a start date on all contracts')"
            :help-info="$t('A start date will be required on any new contracts')"
            class="input-wrapper"
            @update:model-value="save('require_contract_date_start', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'require_contract_date_start' && saving"
              :failed="latestClickedItemKey === 'require_contract_date_start' && savingFailed"
              size="sm"
            />
          </div>
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.requireContractDateEnd"
            :label="$t('Require to set a end date on all contracts')"
            :help-info="$t('A end date will be required on any new contracts')"
            class="input-wrapper"
            @update:model-value="save('require_contract_date_end', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'require_contract_date_end' && saving"
              :failed="latestClickedItemKey === 'require_contract_date_end' && savingFailed"
              size="sm"
            />
          </div>
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.poNumberMandatory"
            :label="$t('Require PO number on contracts')"
            :help-info="$t('A Purchase Order number will be required on all hires')"
            class="input-wrapper"
            @update:model-value="save('po_number_mandatory', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'po_number_mandatory' && saving"
              :failed="latestClickedItemKey === 'po_number_mandatory' && savingFailed"
              size="sm"
            />
          </div>
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.enableMultiCurrencyContracts"
            :label="$t('Enable multi-currency contracts')"
            :help-info="
              $t(
                'This will allow your team to create and offer contracts to your trusted contacts in their preferred currency',
              )
            "
            class="input-wrapper"
            @update:model-value="save('disable_multi_currency_contracts', !$event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'disable_multi_currency_contracts' && saving"
              :failed="latestClickedItemKey === 'disable_multi_currency_contracts' && savingFailed"
              size="sm"
            />
          </div>
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.includeCancelledContractsInExports"
            :label="$t('Include cancelled contracts in data exports')"
            :help-info="$t('Cancelled contracts are not included in data exports by default')"
            class="input-wrapper"
            @update:model-value="save('include_cancelled_contracts_in_exports', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'include_cancelled_contracts_in_exports' && saving"
              :failed="
                latestClickedItemKey === 'include_cancelled_contracts_in_exports' && savingFailed
              "
              size="sm"
            />
          </div>
        </div>
      </div>
    </BaseCard>

    <BaseCard>
      <template #card-header>
        <h2>{{ $t('Billing settings') }}</h2>
      </template>
      <div class="m-t-20">
        <div>
          <EditSettingsOption
            v-model="userState.poNumberBillMandatory"
            :label="$t('Require PO number on bills')"
            :help-info="$t('A Purchase Order number will be required on all bills before approval')"
            class="input-wrapper"
            @update:model-value="save('po_number_bill_mandatory', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'po_number_bill_mandatory' && saving"
              :failed="latestClickedItemKey === 'po_number_bill_mandatory' && savingFailed"
              size="sm"
            />
          </div>
        </div>

        <div>
          <EditSettingsOption
            v-model="userState.requireTimesheetOnBills"
            :label="$t('Require timesheets on all bills')"
            :help-info="$t('This will ensure that every bill you receive must include a timesheet')"
            class="input-wrapper"
            @update:model-value="save('require_timesheet_on_bills', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'require_timesheet_on_bills' && saving"
              :failed="latestClickedItemKey === 'require_timesheet_on_bills' && savingFailed"
              size="sm"
            />
          </div>
        </div>

        <!-- This was set to false originally - LJ  -->
        <div v-if="false">
          <EditSettingsOption
            v-model="userState.dualApprovalBills"
            :label="$t('Dual approval on bill timesheets')"
            :help-info="
              $t(
                'approval of timesheet on a bill requires approval by two team members prior to payment.',
              )
            "
            class="input-wrapper"
            @update:model-value="save('dual_approval_bills', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'dual_approval_bills' && saving"
              :failed="latestClickedItemKey === 'dual_approval_bills' && savingFailed"
              size="sm"
            />
          </div>
        </div>

        <div>
          <EditSettingsOption
            v-model="userState.expenseReportsEnabled"
            :label="$t('Enable expense reports')"
            :help-info="
              $t(
                'Allow a freelancer to submit expense reports on a bill. They will need to upload proof of each expense submitted.',
              )
            "
            class="input-wrapper"
            @update:model-value="save('expense_reports_enabled', $event)"
          />
          <div class="input-wrapper network-loader">
            <NetworkLoader
              :loader="latestClickedItemKey === 'expense_reports_enabled' && saving"
              :failed="latestClickedItemKey === 'expense_reports_enabled' && savingFailed"
              size="sm"
            />
          </div>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import BaseCard from '@/components/BaseCard.vue'
import enums from '@/utils/enum'
import EditSettingsOption from '@/components/EditSettingsOption.vue'
import NetworkLoader from '@/components/NetworkLoader.vue'

export default {
  components: {
    BaseCard,
    NetworkLoader,
    EditSettingsOption,
  },
  props: {
    isEnterpriseAccount: { type: Boolean, default: false },
    projectTracking: { type: Boolean, default: false },
    requiredJobBudget: { type: Boolean, default: false },
    includeCancelledContractsInExports: { type: Boolean, default: false },
    requireContractDateStart: { type: Boolean, default: false },
    requireContractDateEnd: { type: Boolean, default: false },
    poNumberMandatory: { type: Boolean, default: false },
    dualApprovalBills: { type: Boolean, default: false },
    poNumberBillMandatory: { type: Boolean, default: false },
    requirePeriodOnBills: { type: Boolean, default: false },
    requireTimesheetOnBills: { type: Boolean, default: false },
    expenseReportsEnabled: { type: Boolean, default: false },
    enableMultiCurrencyContracts: { type: Boolean, default: false },
  },
  data() {
    return {
      userHasInteractedWithContents: false,
      userState: this.initialUserState(),
      latestClickedItemKey: null,
      saving: false,
      savingFailed: false,
    }
  },
  methods: {
    initialUserState() {
      return {
        projectTracking: this.projectTracking,
        requiredJobBudget: this.requiredJobBudget,
        enableMultiCurrencyContracts: this.enableMultiCurrencyContracts,
        includeCancelledContractsInExports: this.includeCancelledContractsInExports,
        requireContractDateStart: this.requireContractDateStart,
        requireContractDateEnd: this.requireContractDateEnd,
        poNumberMandatory: this.poNumberMandatory,
        poNumberBillMandatory: this.poNumberBillMandatory,
        dualApprovalBills: this.dualApprovalBills,
        requirePeriodOnBills: this.requirePeriodOnBills,
        requireTimesheetOnBills: this.requireTimesheetOnBills,
        expenseReportsEnabled: this.expenseReportsEnabled,
      }
    },
    async save(key, value) {
      this.latestClickedItemKey = key
      this.savingFailed = false
      this.saving = true

      try {
        await this.$apollo.mutate({
          mutation: gql`mutation (
                $globalId: ID!,
              ) {
              updateCompany(input: {
                  globalId: $globalId,
                  settings: {
                        ${key}: ${value}
                  }
              }) {
                globalId
                settings {
                    ${key}
                }
              }
            }`,
          variables: {
            globalId: this.account.globalId,
          },
        })

        this.saving = false
      } catch (e) {
        this.saving = false
        this.savingFailed = true
        this.saveFailedActions()
      }
    },
    saveFailedActions() {
      this.$store.commit('sendNotification', {
        type: enums.notification.types.ERROR,
        sender: this,
        severity: enums.notification.severity.CRITICAL,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.network-loader {
  margin-top: -7px;
}
</style>
